import { Box, Display, Heading4, useTheme, Body2 } from '@gousto-internal/citrus-react'
import React, { useState } from 'react'
import Slider from 'react-slick'
import { Arrow } from './CarouselArrow'
import {
  CuratedBoxCarouselItem,
  CuratedBox as CuratedBoxType,
} from './Components/CuratedBoxCarouseItem/CuratedBoxCarouselItem'
import { getCarouselResponsiveElements } from './CuratedBoxCarouselStyling'
import { useSelector } from 'react-redux'
import Immutable from 'immutable'
import { getCuratedBoxesRecipes, CuratedBoxesRecipesType } from './utils/getCuratedBoxesRecipes'
import { queueTrackingEvent } from '@library/tracking-integration'

import './slick-theme.css'
import './slick.css'

type CuratedBoxCarouselProps = {
  curatedBoxesMethods: {
    getRecipes: () => Immutable.Map<string, string>
    getRecipeStock: {
      isRecipeOutOfStock: (id: string) => boolean
    }
    getCuratedBoxes: () => CuratedBoxesRecipesType
  }
  handleOnClickForCuratedBox: () => void
}

export const CuratedBoxCarousel = ({
  curatedBoxesMethods,
  handleOnClickForCuratedBox,
}: CuratedBoxCarouselProps) => {
  const [carouselIsMoving, setCarouselIsMoving] = useState(false)
  const { theme } = useTheme()
  const carouselResponsiveElements = getCarouselResponsiveElements(theme.tokens.breakpoints)
  const { isRecipeOutOfStock } = curatedBoxesMethods.getRecipeStock
  const allRecipes = useSelector(curatedBoxesMethods.getRecipes)
  const curatedBoxesRecipes = useSelector(curatedBoxesMethods.getCuratedBoxes)
  const recipesToJS = allRecipes.toJS()
  const carouselData = getCuratedBoxesRecipes(recipesToJS, curatedBoxesRecipes, isRecipeOutOfStock)
  const mininumRecipesForCuratedBoxes = 4

  const trackChangeSelectedCarouselItem = (oldIndex: number, newIndex: number) => {
    const newlySelectedCuratedBox = carouselData?.[newIndex]?.title
    const trackingData = {
      action: 'curated_box_carousel_selected_item_changed' as const,
      properties: {
        oldIndex,
        newIndex,
        curatedBoxCategory: newlySelectedCuratedBox,
      },
    }
    queueTrackingEvent<typeof trackingData>(trackingData)
  }

  return (
    <>
      {!!carouselData.length && (
        <Box paddingH={[1, null, 0, null, 2]}>
          <Box paddingH={[2, null, 0]} paddingV={[2, null, 4]}>
            <Heading4>Get a selection</Heading4>
            <Body2>Picked and loved by our chefs to save you time picking your first box</Body2>
          </Box>
          <Box>
            <Box display={Display.Block} paddingH={[2, null, 0]}>
              <Slider
                responsive={carouselResponsiveElements}
                arrows
                prevArrow={<Arrow side={'arrowLeft'} />}
                nextArrow={<Arrow side={'arrowRight'} />}
                variableWidth
                swipeToSlide
                beforeChange={(oldIndex: number, newIndex: number) => {
                  setCarouselIsMoving(true)
                  trackChangeSelectedCarouselItem(oldIndex, newIndex)
                }}
                afterChange={() => setCarouselIsMoving(false)}
              >
                {carouselData.map((item: CuratedBoxType, index: number) => (
                  <Box
                    display={Display.Block}
                    key={item.curatedBoxCollectionId}
                    paddingRight={2}
                    height={['220px', null, '234px', '312px']}
                  >
                    {item.topInStockRecipes.length >= mininumRecipesForCuratedBoxes && (
                      <CuratedBoxCarouselItem
                        carouselItem={item}
                        handleOnClick={handleOnClickForCuratedBox}
                        index={index}
                        carouselIsMoving={carouselIsMoving}
                      />
                    )}
                  </Box>
                ))}
              </Slider>
            </Box>
          </Box>
          <Box paddingH={[2, null, 0]} paddingV={2}>
            <Heading4>Browse our full menu</Heading4>
            <Body2>{`Choose from this week's menu of 75+ recipes`}</Body2>
          </Box>
        </Box>
      )}
    </>
  )
}
